import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Microphone, Smiley, Stop } from '@phosphor-icons/react';
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import { toast } from 'sonner';

const SpeechRecognition =
  typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

// Web Speech API dictation that appends the transcript to the current message.
const useDictation = (inputMessage, setInputMessage, onRecordingChange) => {
  const [isRecording, setRecording] = useState(false);
  const recognitionRef = useRef(null);
  const baseRef = useRef('');

  const setIsRecording = useCallback((v) => { setRecording(v); onRecordingChange?.(v); }, [onRecordingChange]);

  const stop = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    recognitionRef.current = null;
    setIsRecording(false);
  }, [setIsRecording]);

  const start = useCallback(() => {
    if (!SpeechRecognition) {
      toast.error("Your browser doesn't support voice-to-text. Try Chrome or Edge.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';
    baseRef.current = inputMessage ? `${inputMessage} ` : '';

    recognition.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t;
        else interimText += t;
      }
      if (finalText) baseRef.current = `${baseRef.current}${finalText} `;
      setInputMessage(baseRef.current + interimText);
    };
    recognition.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        toast.error('Microphone permission denied. Enable it in browser settings.');
      } else if (e.error !== 'no-speech' && e.error !== 'aborted') {
        toast.error(`Voice error: ${e.error}`);
      }
      stop();
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setIsRecording(false);
    };
    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
      toast.success('Listening... speak now', { duration: 1500 });
    } catch {
      toast.error('Could not start voice input');
      stop();
    }
  }, [inputMessage, setInputMessage, setIsRecording, stop]);

  useEffect(() => () => { try { recognitionRef.current?.abort(); } catch { /* noop */ } }, []);
  // Message was sent/cleared mid-dictation: start the next transcript fresh.
  useEffect(() => { if (!inputMessage) baseRef.current = ''; }, [inputMessage]);

  return { isRecording, toggle: () => (isRecording ? stop() : start()), stop };
};

export const ChatTools = ({ inputMessage, setInputMessage, onRecordingChange, testIdPrefix = '' }) => {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const { isRecording, toggle } = useDictation(inputMessage, setInputMessage, onRecordingChange);
  const tid = (id) => `${testIdPrefix}${id}`;

  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="ghost" size="sm" onClick={toggle}
        className={`hover:bg-[#F4F4F5] transition-colors ${isRecording ? 'bg-[#FF3333] text-white hover:bg-[#E62A2A] animate-pulse' : ''}`}
        data-testid={tid('voice-note-button')}
        aria-label={isRecording ? 'Stop recording' : 'Start voice dictation'}
        title={SpeechRecognition ? (isRecording ? 'Stop dictation' : 'Voice to text (Chrome / Edge)') : 'Voice input not supported in this browser'}>
        {isRecording ? <Stop size={18} weight="fill" /> : <Microphone size={18} />}
      </Button>
      <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="hover:bg-[#F4F4F5] transition-colors" data-testid={tid('emoji-button')} aria-label="Open emoji picker">
            <Smiley size={18} />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="p-0 border-[#E4E4E7] w-auto" data-testid={tid('emoji-picker-panel')}>
          <EmojiPicker onEmojiClick={(e) => { setInputMessage((prev) => (prev || '') + e.emoji); setEmojiOpen(false); }}
            emojiStyle={EmojiStyle.NATIVE} theme={Theme.LIGHT} width={320} height={380}
            skinTonesDisabled previewConfig={{ showPreview: false }} lazyLoadEmojis />
        </PopoverContent>
      </Popover>
      {isRecording && (
        <span className="text-xs text-red-600 font-mono" data-testid={tid('voice-listening-indicator')}>● Listening…</span>
      )}
    </div>
  );
};
